import React, {forwardRef, Children} from 'react';

import {hashedClasses} from '@pb/kit-util-with-css-module';

import {ItemIcon} from './item-icon';
import {MenuItemBase} from './menu-item-base';
import {MenuItemCaption} from './menu-item-caption';
import {MenuItemMeta} from './menu-item-meta.component';
import styles from './menu.module.styl';

import type {MenuItemBaseProps} from './menu-item-base';
import type {IconSize, IconType} from '@pb/kit-component-icon';
import type {ReactNode} from 'react';


const hashedClassNames = hashedClasses(styles);

export interface MenuItemProps extends Omit<MenuItemBaseProps, 'children'> {
    /**
     * This caption will be displayed for the item
     */
    caption?: ReactNode,

    /**
     * This icon will appear before children
     */
    iconBefore?: IconType,

    /**
     * This icon will appear after children
     */
    iconAfter?: IconType,

    /**
     * Item Icon size
     */
    iconSize?: IconSize,

    /**
     * Item has a separate line
     */
    separated?: boolean,

    /**
     * Replace icon with loader
     */
    loader?: boolean,

    /**
     * KeyDown and Click event handler
     */
    onClick?: (event: React.MouseEvent<HTMLLIElement> | React.KeyboardEvent<HTMLLIElement>) => unknown,

    /**
     * Special meta-information (node or array of nodes) that will be placed at the end of the item
     */
    meta?: ReactNode,

    /**
     * Custom content rendering
     */
    children?: ReactNode | ((props: MenuItemProps) => ReactNode),
}

/**
 * Renders a single Item in the Menu List
 * */
export const MenuItem = forwardRef<HTMLLIElement, MenuItemProps>((props, ref) => {
    const {
        disabled,
        loader,
        separated,
        iconBefore,
        iconAfter,
        iconSize,
        children,
        onClick,
        onKeyDown,
        meta,
        caption,
        ...rootProps
    } = props;

    const metaCount = meta ? Children.count(meta) : 0;

    const gridTemplate = metaCount > 1
        ? [
                iconBefore && 'auto',
                `minmax(0, ${metaCount}fr)`,
                ...new Array(metaCount).fill('1fr'),
                iconAfter && 'auto',
            ].filter(Boolean).join(' ')
        : undefined;

    const item = (
        <MenuItemBase
            disabled={disabled}
            {...rootProps}
            style={{
                ...rootProps.style,
                ...gridTemplate ? {display: 'grid'} : null,
                gridTemplateColumns: gridTemplate,
            }}
            onKeyDown={event => {
                if (disabled || loader) {
                    return;
                }
                onKeyDown?.(event);

                if (event.key === 'Enter') {
                    onClick?.(event);
                }
            }}
            onClick={e => {
                if (disabled || loader) {
                    return;
                }

                onClick?.(e);
            }}
            ref={ref}>
            {iconBefore ? (
                <ItemIcon
                    icon={iconBefore}
                    iconSize={iconSize}
                    loader={loader}
                />
            ) : null}
            {caption ? (
                <MenuItemCaption>{caption}</MenuItemCaption>
            ) : null}
            {typeof children === 'function' ? children(props) : children}
            {metaCount > 0 ? Children.map(meta, (metaItem, index) => (
                <MenuItemMeta key={`menu-item-meta-${index}`}>
                    {metaItem}
                </MenuItemMeta>
            )) : null}
            {iconAfter ? (
                <ItemIcon
                    icon={iconAfter}
                    iconSize={iconSize}
                    loader={loader}
                    className={hashedClassNames.right}
                />
            ) : null}
        </MenuItemBase>
    );

    return separated ? (
        <>
            <li role="separator" className={hashedClassNames.separator}/>
            {item}
        </>
    ) : item;
});

MenuItem.displayName = 'MenuItem';
